import { useCallback, useEffect, useRef, useState } from 'react';
import useSWR from 'swr';
import api from '../../../services/api';
import { fetcher } from '../../../services/fetcher';
import { authService } from '../../../services/authService';
import type { EmailUiPreferences } from '../utils/emailUiPreferencesStorage';
import {
  loadEmailUiPreferencesFromLocal,
  mergeEmailUiPreferences,
  saveEmailUiPreferencesToLocal,
} from '../utils/emailUiPreferencesStorage';

const SAVE_DEBOUNCE_MS = 800;
let pendingSaveTimer: ReturnType<typeof setTimeout> | null = null;
let pendingSavePayload: EmailUiPreferences | null = null;

function scheduleRemoteSave(prefs: EmailUiPreferences): void {
  pendingSavePayload = prefs;
  if (pendingSaveTimer) clearTimeout(pendingSaveTimer);
  pendingSaveTimer = setTimeout(() => {
    pendingSaveTimer = null;
    const payload = pendingSavePayload;
    pendingSavePayload = null;
    if (!payload || !authService.getToken()) return;
    void api.patch('/settings/user/email-ui-preferences', payload).catch(() => {
      /** transient — local copy remains */
    });
  }, SAVE_DEBOUNCE_MS);
}

function prefsEqual(a: EmailUiPreferences, b: EmailUiPreferences): boolean {
  return (
    a.sidebarCollapsed === b.sidebarCollapsed &&
    a.sidebarWidth === b.sidebarWidth &&
    a.splitterPosition === b.splitterPosition &&
    a.threadedView === b.threadedView &&
    a.listSortBy === b.listSortBy &&
    a.detailHeaderCollapsed === b.detailHeaderCollapsed &&
    a.bodySplitPercent === b.bodySplitPercent &&
    a.libraryFolderSplitPercent === b.libraryFolderSplitPercent &&
    a.displayPrefs.displayUnreadCountBadge === b.displayPrefs.displayUnreadCountBadge &&
    a.displayPrefs.displayNewMessageButton === b.displayPrefs.displayNewMessageButton &&
    a.displayPrefs.displaySyncButton === b.displayPrefs.displaySyncButton
  );
}

export function useEmailUiPreferences() {
  const token = authService.getToken();
  const { data: remote, mutate } = useSWR<EmailUiPreferences | null>(
    token ? '/settings/user/email-ui-preferences' : null,
    fetcher,
    { revalidateOnFocus: false, dedupingInterval: 30_000 },
  );

  const [prefs, setPrefs] = useState<EmailUiPreferences>(() => loadEmailUiPreferencesFromLocal());
  const remoteAppliedRef = useRef(false);

  useEffect(() => {
    if (remote === undefined || remoteAppliedRef.current) return;
    remoteAppliedRef.current = true;
    const local = loadEmailUiPreferencesFromLocal();
    if (remote === null) {
      setPrefs(local);
      saveEmailUiPreferencesToLocal(local);
      scheduleRemoteSave(local);
      return;
    }
    const merged = mergeEmailUiPreferences(local, remote);
    setPrefs(merged);
    saveEmailUiPreferencesToLocal(merged);
  }, [remote]);

  const updatePrefs = useCallback(
    (patch: Partial<EmailUiPreferences>) => {
      setPrefs((prev) => {
        const next = mergeEmailUiPreferences(prev, patch);
        if (prefsEqual(prev, next)) return prev;
        saveEmailUiPreferencesToLocal(next);
        scheduleRemoteSave(next);
        void mutate(next, { revalidate: false });
        return next;
      });
    },
    [mutate],
  );

  return {
    prefs,
    updatePrefs,
    isRemoteSynced: remoteAppliedRef.current,
  };
}

export type { EmailUiPreferences };
